import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useMarketStore } from '../store/marketStore';
import { useSymbolStore, SymbolDefinition } from '../store/symbolStore';
import { normalizeToBase, resolveActiveSymbol } from '../utils/symbolUtils';
import './SymbolSelector.css';

interface SymbolSelectorProps {
  onSymbolChange?: (symbol: string) => void;
}

const SymbolSelector: React.FC<SymbolSelectorProps> = ({ onSymbolChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const currentSymbol = useMarketStore((state) => state.symbol);
  const setSymbol = useMarketStore((state) => state.setSymbol);
  const symbols = useSymbolStore((state) => state.symbols);
  const activeSymbols = useSymbolStore((state) => state.activeSymbols);
  const enabledSymbols = symbols.filter(s => s.enabled);

  // Group symbols by exchange for display
  const groupedSymbols = enabledSymbols.reduce((acc, sym) => {
    const group = sym.exchange || 'Other';
    if (!acc[group]) acc[group] = [];
    acc[group].push(sym);
    return acc;
  }, {} as Record<string, SymbolDefinition[]>);

  // Get display name from current symbol (strip contract month)
  const displaySymbol = (currentSymbol && normalizeToBase(currentSymbol)) || 'Select';

  const handleSymbolSelect = (sym: SymbolDefinition) => {
    // THIS is where the June contract got onto the chart. The registry is
    // user-edited configuration persisted in Redis, so `sym.fullSymbol` is
    // whatever contract was current when somebody last saved it -- in
    // September 2026 the NQ row still read `NQM6.CME`, three months expired.
    // Clicking it subscribed to a dead contract and the engine obligingly
    // opened a watcher on its SCID file. Resolve against what the engine is
    // actually streaming instead; the registry supplies the base, never the
    // contract month.
    const resolved = resolveActiveSymbol(sym.fullSymbol, activeSymbols) ?? sym.fullSymbol;
    setSymbol(resolved);
    onSymbolChange?.(resolved);
    setIsOpen(false);
  };

  return (
    <div className="symbol-selector">
      <button
        className="symbol-selector-button action-bar-btn primary"
        onClick={() => setIsOpen(!isOpen)}
        title="Change Symbol"
      >
        <span className="symbol-selector-value">{displaySymbol}</span>
        <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor" className="symbol-selector-arrow">
          <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" />
        </svg>
      </button>

      {isOpen && createPortal(
        <>
          <div className="symbol-selector-overlay" onClick={() => setIsOpen(false)} />
          <div className="symbol-selector-modal">
            <div className="symbol-selector-header">
              <h3>Select Symbol</h3>
              <button className="symbol-selector-close" onClick={() => setIsOpen(false)}>
                ×
              </button>
            </div>
            <div className="symbol-selector-content">
              {Object.entries(groupedSymbols).map(([group, groupSymbols]) => (
                <div key={group} className="symbol-group">
                  <div className="symbol-group-header">{group}</div>
                  <div className="symbol-grid">
                    {groupSymbols.map((sym) => (
                      <button
                        key={sym.base}
                        className={`symbol-card ${currentSymbol === sym.fullSymbol ? 'active' : ''}`}
                        onClick={() => handleSymbolSelect(sym)}
                      >
                        <span className="symbol-card-base">{sym.base}</span>
                        <span className="symbol-card-label">{sym.label}</span>
                        <span className="symbol-card-tick">
                          {sym.tickSize} / ${sym.tickValue}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
              {enabledSymbols.length === 0 && (
                <div className="symbol-empty">
                  No symbols available. Check backend configuration.
                </div>
              )}
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};

export default SymbolSelector;
